import { formatDistanceToNow } from 'date-fns';
import type {
  CalendarConnectionsResponse,
  ImportListResponse,
  ImportSummary,
  MailboxChannelResponse,
  PhoneStatusResponse,
  SharePointStatusResponse,
} from '@biasafe.models';
import { mailboxHealth } from '@/features/Clients/mailbox/mailboxHealth';
import { phoneHealth } from '@/features/Phone/phoneHealth';
import { sharepointHealth } from '@/features/SharePoint/sharepointCopy';

/**
 * What a tile on the Integrations page says about one connection.
 *
 * THE TILE READS THE SAME FACTS THE DIALOG'S CARDS READ, and says them before
 * anyone clicks. It used to draw "Connect" on every provider, so a deployment
 * with no Google credential offered a button that led to a dead end, and a
 * revoked or failing grant looked the same as a healthy one.
 *
 * `unconfigured` wins over everything except a connection that already exists
 * on a mailbox: nothing the user can do fixes a missing server credential, so
 * the tile carries no button at all (see CalendarConnectionsCard for the same
 * rule inside the dialog).
 */
export type TileState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'unconfigured' }
  | { kind: 'connect' }
  /** Another provider's mailbox is connected; it has to go first. */
  | { kind: 'switch'; other: string }
  | { kind: 'connected' }
  | { kind: 'reconnect'; message: string };

type QueryLike<T> = { isLoading?: boolean; isError?: boolean; data?: T };

export const MAILBOX_LABEL = { GOOGLE: 'Gmail', MICROSOFT: 'Outlook' } as const;

export function calendarTileState(
  query: QueryLike<CalendarConnectionsResponse>,
  provider: 'GOOGLE' | 'OUTLOOK',
): TileState {
  if (query.isLoading) return { kind: 'loading' };
  if (query.isError) return { kind: 'error' };
  const data = query.data;
  if (!data) return { kind: 'loading' };
  const configured =
    provider === 'GOOGLE' ? data.google_configured : data.microsoft_configured;
  if (!configured) return { kind: 'unconfigured' };
  const row = data.connections.find((c) => c.provider === provider);
  if (row?.status === 'REAUTH_REQUIRED') {
    return {
      kind: 'reconnect',
      message: 'This authorisation was withdrawn. Reconnect to resume syncing.',
    };
  }
  return row?.connected ? { kind: 'connected' } : { kind: 'connect' };
}

export function mailboxTileState(
  query: QueryLike<MailboxChannelResponse>,
  provider: 'GOOGLE' | 'MICROSOFT',
): TileState {
  if (query.isLoading) return { kind: 'loading' };
  if (query.isError) return { kind: 'error' };
  const data = query.data;
  if (!data) return { kind: 'loading' };
  const channel = data.channel ?? null;
  if (channel && channel.provider === provider) {
    // The shared Google grant's health (#2322) is the channel's own: a
    // revoked grant, a 403 ("Google refused access") or a failing sync all
    // arrive here as a reason and a reconnect, never as "Connected".
    const health = mailboxHealth(channel);
    return health.kind === 'ok'
      ? { kind: 'connected' }
      : { kind: 'reconnect', message: health.message };
  }
  if (!(data.available_providers ?? []).includes(provider)) {
    return { kind: 'unconfigured' };
  }
  if (channel) {
    return {
      kind: 'switch',
      other: channel.provider === 'GOOGLE' ? 'Gmail' : 'Outlook',
    };
  }
  return { kind: 'connect' };
}

export function stateLabel(state: TileState): string {
  switch (state.kind) {
    case 'loading':
      return 'Loading…';
    case 'error':
      return 'Check connection';
    case 'connected':
      return 'Connected';
    case 'reconnect':
      return 'Reconnect';
    // Visible text, not only an aria-label: "Connect" here promised a button
    // that the tile does not have.
    case 'unconfigured':
      return 'Not set up';
    default:
      return 'Connect';
  }
}

/** Firm-wide connection state; management opens inside Integrations. */
export type PhoneTileState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'unconfigured' }
  | {
      kind: 'not_connected' | 'connected' | 'attention';
      /** What the tile says about it, in words. */
      message: string;
      /** An org admin can manage the connection; a member can only read it. */
      canManage: boolean;
      demo: boolean;
    };

export function phoneTileState(
  query: QueryLike<PhoneStatusResponse>,
  isAdmin: boolean,
): PhoneTileState {
  if (query.isLoading) return { kind: 'loading' };
  if (query.isError) return { kind: 'error' };
  const data = query.data;
  if (!data) return { kind: 'loading' };
  if (!data.configured) return { kind: 'unconfigured' };
  // Both locks, as on the card: the server's flag and the session's role.
  const canManage = isAdmin && (data.canManage ?? false);
  const demo = data.mockMode ?? false;
  const connection = data.connection ?? null;
  if (!connection) {
    return {
      kind: 'not_connected',
      message: canManage
        ? 'Not connected. Connect it for your firm.'
        : 'Not connected. An organization admin connects it for the whole firm.',
      canManage,
      demo,
    };
  }
  const health = phoneHealth(connection);
  return health.kind === 'ok'
    ? { kind: 'connected', message: 'Connected', canManage, demo }
    : { kind: 'attention', message: health.message, canManage, demo };
}

/** The SharePoint tile: the same shape and the same two locks as the phone's. */
export function sharepointTileState(
  query: QueryLike<SharePointStatusResponse>,
  isAdmin: boolean,
): PhoneTileState {
  if (query.isLoading) return { kind: 'loading' };
  if (query.isError) return { kind: 'error' };
  const data = query.data;
  if (!data) return { kind: 'loading' };
  if (!data.configured) return { kind: 'unconfigured' };
  const canManage = isAdmin && (data.canManage ?? false);
  const demo = data.mockMode ?? false;
  const connection = data.connection ?? null;
  if (!connection) {
    return {
      kind: 'not_connected',
      message: canManage
        ? 'Not connected. Connect it for your firm.'
        : 'Not connected. An organization admin connects it for the whole firm.',
      canManage,
      demo,
    };
  }
  const health = sharepointHealth(connection);
  return health.kind === 'ok'
    ? { kind: 'connected', message: 'Connected', canManage, demo }
    : { kind: 'attention', message: health.message, canManage, demo };
}

/**
 * "Last synced 3 hours ago" for a timestamp the API already returns, or null
 * when there is none (never synced) or it does not parse. No line beats a
 * wrong one. Only the mailbox's `last_synced_at`, the calendar's
 * `last_synced_at` and the phone connection's `lastPolledAt` feed this.
 */
export function lastSyncedLine(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return `Last synced ${formatDistanceToNow(at, { addSuffix: true })}`;
}

export function calendarLastSynced(
  query: QueryLike<CalendarConnectionsResponse>,
  provider: 'GOOGLE' | 'OUTLOOK',
): string | null {
  const row = query.data?.connections.find((c) => c.provider === provider);
  return row?.connected ? lastSyncedLine(row.last_synced_at) : null;
}

export function mailboxLastSynced(
  query: QueryLike<MailboxChannelResponse>,
  provider: 'GOOGLE' | 'MICROSOFT',
): string | null {
  const channel = query.data?.channel;
  return channel && channel.provider === provider
    ? lastSyncedLine(channel.last_synced_at)
    : null;
}

export function phoneLastSynced(
  query: QueryLike<PhoneStatusResponse>,
): string | null {
  return lastSyncedLine(query.data?.connection?.lastPolledAt);
}

/**
 * The NBIN Compass tile: the newest COMMITTED position import.
 *
 * AN IMPORT IS A FILE, NOT A CUSTODIAN READING. The tile therefore reports what
 * was uploaded and when, never "connected" or "synced".
 */
export type BookImportTileState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'empty' }
  | { kind: 'latest'; latest: ImportSummary };

export function bookImportTileState(
  query: QueryLike<ImportListResponse>,
): BookImportTileState {
  if (query.isLoading) return { kind: 'loading' };
  if (query.isError) return { kind: 'error' };
  const data = query.data;
  if (!data) return { kind: 'loading' };
  const committed = data.imports.filter(
    (i) => i.status.toUpperCase() === 'COMMITTED',
  );
  if (!committed.length) return { kind: 'empty' };
  // Newest by upload time; the API says newest first, but not relying on it
  // costs one sort and an unordered response would otherwise show a stale book.
  const latest = committed.reduce((a, b) =>
    new Date(b.uploadedAt).getTime() > new Date(a.uploadedAt).getTime() ? b : a,
  );
  return { kind: 'latest', latest };
}

export function bookImportLine(latest: ImportSummary): string {
  const at = new Date(latest.uploadedAt);
  const when = Number.isNaN(at.getTime())
    ? ''
    : `, ${formatDistanceToNow(at, { addSuffix: true })}`;
  return `Last import: ${latest.filename}, as of ${latest.asOf}, ${latest.rowCount} rows${when}`;
}
